/**
 * Los modelos de documento son DATOS, no código.
 *
 * Agregar el DNI de otro país, o el modelo nuevo del año que viene, tiene que
 * ser agregar una entrada acá. Si para soportar un documento hay que tocar la
 * lógica de lectura, el catálogo está mal diseñado.
 *
 * Las cajas van en fracciones de la TARJETA, no de la foto. Es la diferencia que
 * hace que una foto torcida o con fondo de sobra deje de correr las zonas: antes
 * de usar esto hay que haber ubicado y enderezado la tarjeta.
 *
 * Cada caja declara su `procedencia`. Las que dicen "estimada" están esperando
 * que las midamos contra fotos reales; las que dicen "medida" ya fallaron y se
 * corrigieron, y moverlas es volver atrás.
 */
import { Rectangulo } from "./types";
import { pareceRotulo } from "./frente";

export type Cara = "frente" | "dorso";

export interface ZonaDeModelo {
  /** Nombre del campo. Es la clave con la que sale en el JSON. */
  campo: string;
  cara: Cara;
  /** Fracciones 0..1 de la tarjeta enderezada. */
  caja: Rectangulo;
  tipo: "texto" | "mrz" | "codigo2d" | "foto" | "firma";
  /** Alto del renglón impreso en el documento real, en mm. Sirve para medir
   *  legibilidad por geometría, sin depender de que el OCR haya acertado. */
  altoRenglonMm?: number;
  /** No puede salir publicada. Hoy solo el Nº de Trámite. */
  sensible?: boolean;
  /** Preproceso que esta zona necesita para el OCR. Medido, no elegido a gusto. */
  preproceso?: "gris" | "gris+contraste" | "adaptativo";
  procedencia: "medida" | "estimada";
  /** De dónde salió el número, cuando vale la pena recordarlo. */
  nota?: string;
}

export interface Codigo2dDeModelo {
  cara: Cara;
  tipo: "pdf417" | "qr";
  caja?: Rectangulo;
  procedencia: "medida" | "estimada";
  /** La forma del contenido, cuando distingue este código de otro del mismo tipo. */
  forma?: RegExp;
}

export interface ModeloDeDocumento {
  id: string;
  /** ISO 3166-1 alfa-3. */
  pais: string;
  tipo: "id" | "pasaporte" | "licencia";
  nombre: string;
  /** Formato físico: define la relación de lados con la que se busca la tarjeta. */
  formato: "ID-1" | "TD3";
  /** Zona legible por máquina, si tiene. */
  mrz: "TD1" | "TD2" | "TD3" | null;
  codigos2d: Codigo2dDeModelo[];
  /** Texto impreso siempre igual. Es lo que permite reconocer el modelo sin
   *  depender de los datos del titular, que cambian. */
  rotulosFijos: string[];
  zonas: ZonaDeModelo[];
  vigenciaDesde?: string;
  nota?: string;
}

/**
 * DNI argentino tarjeta, la familia anterior al electrónico.
 *
 * ALCANCE VERIFICADO: 2016 a 2025. Tres documentos —uno emitido en 2016, el
 * espécimen de 2020 y uno emitido en 2025— dan las mismas zonas dentro del 2%
 * del ancho de la tarjeta, medidos por métodos independientes. Eso prueba que
 * el formato es estable en ese rango.
 *
 * LO QUE NO ESTÁ VERIFICADO: de 2012 a 2015 no tenemos ningún ejemplar. El
 * identificador dice "2012" por la fecha en que arrancó la familia, pero eso es
 * una suposición: nadie midió una tarjeta de esos años.
 *
 * Y no es académico. La tarjeta de mayores de 14 vale 15 años desde su
 * expedición, así que los ejemplares del hueco vencen entre 2027 y 2030: el de
 * 2012 el año que viene, el de 2015 en 2030. La ventana se cierra sola.
 *
 * Pero no desaparece, y por una decisión de producto: un documento vencido NO
 * impide el envío a SADAIC, porque lo que se pide es la foto del documento y no
 * que esté vigente. Así que una tarjeta de 2012 vencida se va a seguir subiendo
 * después de 2027, y si su distribución fuera distinta la leeríamos con el
 * template equivocado.
 *
 * La vigencia, de todos modos, no la decide el modelo: la decide la fecha de
 * vencimiento impresa, que se lee del propio documento.
 *
 * ---
 *
 * DNI argentino tarjeta, familia 2012.
 *
 * Las tres zonas del dorso vienen de fracciones que YA ESTÁN MEDIDAS y
 * funcionando en `imagePrep.ts`: el domicilio solo aparece recortando la mitad
 * de arriba, el CUIL solo con la banda del medio, y el MRZ vive en la franja de
 * abajo. Estaban expresadas como fracciones de la FOTO y acá pasan a ser
 * fracciones de la TARJETA, que es lo que siempre quisieron ser: coincidían
 * porque las fotos de prueba venían recortadas justo al documento.
 *
 * Las del frente están estimadas. Hoy el frente se lee buscando las palabras por
 * geometría, sin plantilla, así que nunca hubo que medirlas.
 */
const AR_DNI_2012: ModeloDeDocumento = {
  id: "ar-dni-2012",
  pais: "ARG",
  tipo: "id",
  nombre: "DNI tarjeta (familia 2012)",
  formato: "ID-1",
  mrz: "TD1",
  vigenciaDesde: "2012",
  rotulosFijos: [
    "REPUBLICA ARGENTINA",
    "APELLIDO",
    "NOMBRE",
    "SEXO",
    "NACIONALIDAD",
    "FECHA DE NACIMIENTO",
    "DOMICILIO",
    "TRAMITE",
  ],
  // El contenido empieza por el trámite, once dígitos. MEDIDO sobre el corpus: el
  // dorso de la tarjeta 2009 lleva un PDF417 de la misma proporción, pero su
  // contenido empieza con «@», y sin esta forma anclaba una «tarjeta 2012» sobre él.
  codigos2d: [{ cara: "frente", tipo: "pdf417", procedencia: "medida", forma: /^\d{11}@/ }],
  zonas: [
    // ----- Frente -----
    // MEDIDO sobre DNI-Argentina-2020-Frente.jpg (RENAPER emisión 003), con el
    // borde de la tarjeta en (55,25)-(1955,1215) de la imagen: relación de
    // lados 1,596 contra 1,586 nominal, que es lo que confirma el encuadre.
    // Un solo espécimen: confirmar contra otras fotos antes de apretar más.
    { campo: "foto", cara: "frente", tipo: "foto",
      caja: { x: 0.060, y: 0.223, w: 0.269, h: 0.596 }, procedencia: "medida" },
    { campo: "apellido", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.374, y: 0.166, w: 0.187, h: 0.066 }, procedencia: "medida" },
    { campo: "nombres", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.374, y: 0.311, w: 0.187, h: 0.071 }, procedencia: "medida" },
    { campo: "sexo", cara: "frente", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.374, y: 0.458, w: 0.105, h: 0.063 }, procedencia: "medida",
      nota: "El valor va DEBAJO del rótulo, no al lado: el frente es una grilla. En orden de lectura se cruzan cinco palabras entre 'Sexo' y su 'M'." },
    { campo: "nacionalidad", cara: "frente", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.508, y: 0.458, w: 0.253, h: 0.063 }, procedencia: "medida" },
    { campo: "fechaNacimiento", cara: "frente", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.374, y: 0.555, w: 0.345, h: 0.067 }, procedencia: "medida" },
    { campo: "fechaVencimiento", cara: "frente", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.374, y: 0.739, w: 0.345, h: 0.067 }, procedencia: "medida" },
    { campo: "numeroDocumento", cara: "frente", tipo: "texto", altoRenglonMm: 3.5,
      caja: { x: 0.060, y: 0.836, w: 0.237, h: 0.105 }, procedencia: "medida" },
    // Lo que se tapa: el número de trámite impreso y el código de barras, que lo
    // LLEVA ADENTRO —verificado en el payload de tres documentos— y no se puede
    // tapar a medias: la corrección de errores lo reconstruye. Es todo o nada.
    // El código se decodifica ANTES de taparlo, así que no perdemos ningún dato.
    //
    // MEDIDO sobre 13 frentes reales, con la tarjeta ubicada por las esquinas del
    // código (M1): el número impreso cae entre x 0,342 y 0,589 e y 0,858 y 0,933;
    // la fecha de vencimiento termina en y 0,81 como mucho. La franja anterior
    // (desde x 0,33, y 0,795) cortaba esa fecha en cuatro de cada cinco fotos.
    // Cuando el OCR encuentra el número que dio el código, se tapa ese lugar y no
    // esta caja (ver tramite.ts): en el diseño nuevo, de números más grandes, el
    // documento llega hasta x 0,37.
    { campo: "numeroTramite", cara: "frente", tipo: "texto", altoRenglonMm: 2.0,
      caja: { x: 0.335, y: 0.845, w: 0.265, h: 0.1 }, sensible: true,
      procedencia: "medida",
      nota: "Permite hacer gestiones a nombre del titular. Entre el documento (termina en 0,297; 0,37 en el diseño nuevo) y el vencimiento (termina en 0,81)." },
    { campo: "pdf417", cara: "frente", tipo: "codigo2d",
      caja: { x: 0.600, y: 0.811, w: 0.380, h: 0.145 }, procedencia: "medida",
      nota: "La caja del símbolo: de ella sale la tarjeta entera (anclaje.ts). No se toca: es la referencia de todo lo demás." },
    // El código, con un margen, como zona a tapar por plantilla. Con el código
    // decodificado se tapa además su lugar exacto (zonasSensibles); la plantilla
    // se aplica solo con la tarjeta ubicada por un ancla, nunca por el contorno.
    { campo: "codigoDeBarras", cara: "frente", tipo: "codigo2d",
      caja: { x: 0.590, y: 0.800, w: 0.400, h: 0.170 }, sensible: true,
      procedencia: "medida" },

    // ----- Dorso -----
    { campo: "domicilio", cara: "dorso", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0, y: 0, w: 1, h: 0.55 }, preproceso: "gris",
      procedencia: "medida",
      nota: "Medido: el domicilio solo aparece recortando la mitad de arriba. Con el preproceso del MRZ no se lee." },
    { campo: "cuil", cara: "dorso", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0, y: 0.45, w: 1, h: 0.17 }, preproceso: "gris+contraste",
      procedencia: "medida",
      nota: "Medido: solo se lee con la banda del medio ampliada y el contraste estirado." },
    { campo: "mrz", cara: "dorso", tipo: "mrz",
      caja: { x: 0, y: 0.55, w: 1, h: 0.45 }, preproceso: "gris+contraste",
      procedencia: "medida",
      nota: "Medido: franja inferior del 45%." },
  ],
};

/**
 * DNI electrónico argentino, Disposición 55/2026, vigente desde el 1/2/2026.
 *
 * Los CAMPOS salen de la norma, no de mirar una foto. El anexo los enumera:
 *
 *   Anverso (17): escudo, apellido y nombre —hasta 35 caracteres cada uno—,
 *   sexo (1 carácter), número de DNI, fechas de emisión y vencimiento,
 *   nacionalidad, firma, fecha de nacimiento, número de serie grabado por
 *   láser, número CAN e imagen fantasma.
 *
 *   Reverso (8): domicilio (hasta 51 caracteres), lugar de nacimiento —solo la
 *   provincia, hasta 30—, firma del Ministro del Interior, CUIL, NÚMERO DE
 *   TRÁMITE, código QR, zona de lectura mecánica e imagen fantasma.
 *
 * Tres cosas que esto confirma y que antes eran deducciones de una imagen: el
 * trámite está en el reverso, el QR también, y el MRZ igual. Y una que no
 * teníamos: el número CAN va en el anverso.
 *
 * Los 35 caracteres de apellido y de nombre explican por qué la línea 3 del MRZ
 * trunca: tiene 30 para los dos juntos.
 *
 * Lo que la norma NO publica en texto son las POSICIONES —van en imágenes del
 * anexo—, así que las cajas siguen sin medir y el tapado por plantilla no se
 * activa para este modelo.
 *
 * VERIFICADO además: policarbonato con chip NFC sin contacto, fabricado según
 * OACI 9303, seguridad de Nivel 1 actualizada y Niveles 2 y 3 sin cambios. Y el
 * dato que más manda en el diseño: los documentos actuales siguen siendo
 * válidos y el cambio es VOLUNTARIO. Los dos modelos conviven sin fecha de
 * corte; no es una migración.
 */
const AR_DNI_2026: ModeloDeDocumento = {
  id: "ar-dni-2026",
  pais: "ARG",
  tipo: "id",
  nombre: "DNI tarjeta (modelo 2026)",
  formato: "ID-1",
  mrz: "TD1",
  vigenciaDesde: "2026-02-01",
  rotulosFijos: ["REPUBLICA ARGENTINA", "APELLIDO", "NOMBRE", "SEXO", "NACIONALIDAD", "TRAMITE", "CUIL"],
  codigos2d: [{ cara: "dorso", tipo: "qr", procedencia: "estimada" }],
  zonas: [
    { campo: "mrz", cara: "dorso", tipo: "mrz",
      caja: { x: 0, y: 0.55, w: 1, h: 0.45 }, preproceso: "gris+contraste",
      procedencia: "estimada" },
    { campo: "numeroTramite", cara: "dorso", tipo: "texto", altoRenglonMm: 2.0,
      caja: { x: 0.04, y: 0.04, w: 0.30, h: 0.08 }, sensible: true,
      procedencia: "estimada",
      nota: "En este modelo el trámite está en el DORSO. Es el caso que la detección por rótulo no cubría." },
  ],
  nota: "Los campos vienen de la Disposición 55/2026; las posiciones no, porque la norma las publica en imágenes. Faltan fotos de un documento real: las que tenemos traen el QR dibujado, no un QR decodificable.",
};

/**
 * DNI argentino tarjeta, diseño viejo (2009-2012).
 *
 * La tarjeta que acompañaba a la libreta, suprimida en 2012. Rompe tres cosas
 * que dábamos por ciertas mirando solo los formatos posteriores:
 *
 *  1. **El MRZ está en el FRENTE**, debajo de la fecha de vencimiento. En los
 *     formatos siguientes está en el dorso. Cualquier detección de cara que diga
 *     "hay MRZ, entonces es el dorso" se equivoca acá.
 *  2. **El frente no lleva código 2D**; el dorso lleva un PDF417 (visto en el
 *     corpus, con un contenido que empieza con «@»), sin caja medida. Así que el
 *     ancla por código no existe para este formato y la orientación tiene que
 *     salir del MRZ — que está en el frente.
 *  3. **Los rótulos son otros**: "NUMERO DE DOCUMENTO" y no "Documento",
 *     "FECHA DE EXPEDICION" y no "Fecha de emisión", y sobre todo
 *     "FECHA Y LUGAR DE NACIMIENTO" en un solo rótulo del dorso, donde los
 *     formatos nuevos tienen la fecha en el frente y el lugar en el dorso.
 *
 * El Nº de trámite va en el DORSO, como en el 2026 y al revés que en el formato
 * intermedio.
 *
 * Las posiciones son ESTIMADAS: salen de mirar una imagen del documento, no de
 * medirla con el pipeline. El ejemplar que vimos vence el 30/3/2025, o sea que
 * ya está vencido — pero un documento vencido se sigue subiendo, porque a SADAIC
 * se le manda la foto del documento y no se le exige vigencia.
 *
 * CUIDADO con el borde derecho del frente: está tapado en la imagen que vimos,
 * así que no se puede afirmar que no haya nada ahí.
 */
const AR_DNI_2009: ModeloDeDocumento = {
  id: "ar-dni-2009",
  pais: "ARG",
  tipo: "id",
  nombre: "DNI tarjeta, diseño 2009-2012",
  formato: "ID-1",
  mrz: "TD1",
  vigenciaDesde: "2009",
  rotulosFijos: [
    "REPUBLICA ARGENTINA",
    "DOCUMENTO NACIONAL DE IDENTIDAD",
    "REGISTRO NACIONAL DE LAS PERSONAS",
    "APELLIDO",
    "NOMBRE",
    "NUMERO DE DOCUMENTO",
    "SEXO",
    "NACIONALIDAD",
    "FECHA DE EXPEDICION",
    "FECHA DE VENCIMIENTO",
    "NO VALIDO PARA VOTAR",
  ],
  // MEDIDO sobre el corpus: el DORSO lleva un PDF417 de la misma proporción que el del frente
  // 2012, pero su contenido empieza con «@». No ancla (el modelo no declara su caja): sirve para
  // reconocer la cara (caraDeLaFoto).
  codigos2d: [{ cara: "dorso", tipo: "pdf417", procedencia: "medida", forma: /^@/ }],
  zonas: [
    // ----- Frente: rótulo arriba, valor debajo, todo alineado a la izquierda.
    { campo: "apellido", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.079, y: 0.207, w: 0.310, h: 0.059 }, procedencia: "estimada" },
    { campo: "nombres", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.079, y: 0.301, w: 0.310, h: 0.059 }, procedencia: "estimada" },
    { campo: "numeroDocumento", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.079, y: 0.383, w: 0.270, h: 0.059 }, procedencia: "estimada" },
    { campo: "sexo", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.419, y: 0.383, w: 0.090, h: 0.059 }, procedencia: "estimada" },
    { campo: "nacionalidad", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.079, y: 0.465, w: 0.230, h: 0.059 }, procedencia: "estimada" },
    { campo: "fechaEmision", cara: "frente", tipo: "texto", altoRenglonMm: 2.5,
      caja: { x: 0.079, y: 0.553, w: 0.230, h: 0.059 }, procedencia: "estimada" },
    { campo: "fechaVencimiento", cara: "frente", tipo: "texto", altoRenglonMm: 3.0,
      caja: { x: 0.079, y: 0.641, w: 0.260, h: 0.062 }, procedencia: "estimada" },
    { campo: "foto", cara: "frente", tipo: "foto",
      caja: { x: 0.603, y: 0.059, w: 0.285, h: 0.586 }, procedencia: "estimada" },
    // El MRZ ocupa el pie del FRENTE en este diseño.
    { campo: "mrz", cara: "frente", tipo: "mrz",
      caja: { x: 0.060, y: 0.730, w: 0.900, h: 0.240 }, preproceso: "gris+contraste",
      procedencia: "estimada",
      nota: "En el frente, no en el dorso: es lo que distingue a este formato." },

    // ----- Dorso
    { campo: "domicilio", cara: "dorso", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.020, y: 0.020, w: 0.700, h: 0.110 }, procedencia: "estimada" },
    { campo: "fechaYLugarNacimiento", cara: "dorso", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.020, y: 0.150, w: 0.760, h: 0.110 }, procedencia: "estimada",
      nota: "Un solo rótulo para las dos cosas. En los formatos nuevos van separadas y en caras distintas." },
    { campo: "numeroTramite", cara: "dorso", tipo: "texto", altoRenglonMm: 2.2,
      caja: { x: 0.020, y: 0.520, w: 0.380, h: 0.070 }, sensible: true,
      procedencia: "estimada",
      nota: "En el dorso, como en el 2026 y al revés que en el formato intermedio." },
    { campo: "huella", cara: "dorso", tipo: "foto",
      caja: { x: 0.660, y: 0.300, w: 0.250, h: 0.360 }, procedencia: "estimada" },
  ],
  nota: "Posiciones estimadas a partir de una imagen, no medidas. Los ejemplares están vencidos o por vencer, pero se siguen subiendo.",
};

export const CATALOGO: ModeloDeDocumento[] = [AR_DNI_2009, AR_DNI_2012, AR_DNI_2026];

export function modeloPorId(id: string): ModeloDeDocumento | null {
  return CATALOGO.find((m) => m.id === id) ?? null;
}

export function zonasDe(modelo: ModeloDeDocumento, cara: Cara): ZonaDeModelo[] {
  return modelo.zonas.filter((z) => z.cara === cara);
}

export function zonasSensiblesDe(modelo: ModeloDeDocumento): ZonaDeModelo[] {
  return modelo.zonas.filter((z) => z.sensible);
}

/** Pasa una caja en fracciones de tarjeta a píxeles de una imagen enderezada. */
export function cajaEnPixeles(
  caja: Rectangulo,
  anchoPx: number,
  altoPx: number
): Rectangulo {
  return {
    x: Math.round(caja.x * anchoPx),
    y: Math.round(caja.y * altoPx),
    w: Math.round(caja.w * anchoPx),
    h: Math.round(caja.h * altoPx),
  };
}

export interface PistasDeReconocimiento {
  /** Texto que el OCR sacó de la cara, para buscar los rótulos fijos. */
  texto: string;
  /**
   * Las palabras sueltas, si se tienen. Sirven para comparar con tolerancia:
   * el OCR se come letras y el texto corrido nunca trae un rótulo limpio.
   */
  palabras?: { text: string }[];
  /**
   * Códigos 2D encontrados, con la cara donde aparecieron y, si se tiene, su
   * contenido: un código cuyo contenido no tiene la forma que el modelo declara
   * no es el de ese modelo (el PDF417 del dorso 2009 no es el del frente 2012).
   */
  codigos2d?: Array<{ cara: Cara; tipo: "pdf417" | "qr"; contenido?: string }>;
  /** Tipo de MRZ que se pudo parsear, si alguno. */
  mrz?: "TD1" | "TD2" | "TD3" | null;
  /**
   * La cara que se lee. Con ella, solo puntúan los códigos y el MRZ que el modelo
   * lleva en esa cara: los de la otra nunca pueden aparecer, y contarlos le bajaba
   * el puntaje al modelo correcto (un frente 2009 salía 2026 por el PDF417 de su
   * dorso). Sin ella, cuentan los de las dos caras.
   */
  cara?: Cara;
}

/**
 * Reconoce el modelo apoyándose solo en lo invariante: los rótulos impresos, qué
 * códigos aparecen y en qué cara, y el tipo de MRZ. Nunca en los datos del
 * titular, que cambian de documento en documento.
 *
 * Devuelve también la confianza, porque de ella depende algo concreto: sin
 * modelo reconocido no hay plantilla, y sin plantilla el tapado del Nº de
 * Trámite deja de ser una garantía.
 */
export function reconocerModelo(
  pistas: PistasDeReconocimiento,
  modelos: ModeloDeDocumento[] = CATALOGO
): { modelo: ModeloDeDocumento | null; confianza: number; detalle: string[] } {
  const texto = pistas.texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();
  const detalle: string[] = [];

  let mejor: ModeloDeDocumento | null = null;
  let mejorPuntaje = 0;

  for (const m of modelos) {
    let puntaje = 0;
    let total = 0;

    // Con tolerancia, no por coincidencia exacta. Medido: con `includes` sobre
    // el texto corrido, NINGUNO de los cinco documentos reales del corpus se
    // reconocía —el OCR devuelve "REPUBLICAARGENTINA-MERCOSUR" con ruido, o a
    // pedazos— y el reconocimiento de modelo no servía para nada.
    const encontrados = m.rotulosFijos.filter((r) => {
      const clave = r.replace(/\s+/g, "");
      if (texto.includes(r) || texto.replace(/\s+/g, "").includes(clave)) return true;
      // Un rótulo de varias palabras se da por encontrado si TODAS sus palabras
      // aparecen, cada una con la tolerancia de siempre.
      const partes = r.split(/\s+/).filter((p) => p.length >= 4);
      if (partes.length === 0 || !pistas.palabras) return false;
      return partes.every((parte) =>
        pistas.palabras!.some((w) => pareceRotulo(w.text, parte))
      );
    });
    puntaje += encontrados.length;
    total += m.rotulosFijos.length;

    // Qué código aparece y en qué cara distingue los modelos entre sí: el 2012
    // tiene PDF417 al frente y el 2026 un QR al dorso.
    const enEstaCara = (c: Cara) => !pistas.cara || c === pistas.cara;
    for (const c of m.codigos2d) {
      if (!enEstaCara(c.cara)) continue;
      total += 1;
      if (
        pistas.codigos2d?.some(
          (v) => v.cara === c.cara && v.tipo === c.tipo && (!c.forma || v.contenido === undefined || c.forma.test(v.contenido.trim()))
        )
      ) {
        puntaje += 1;
      }
    }

    if (m.mrz && (!pistas.cara || m.zonas.some((z) => z.tipo === "mrz" && z.cara === pistas.cara))) {
      total += 1;
      if (pistas.mrz === m.mrz) puntaje += 1;
    }

    const ratio = total > 0 ? puntaje / total : 0;
    detalle.push(`${m.id}: ${encontrados.length}/${m.rotulosFijos.length} rótulos, puntaje ${ratio.toFixed(2)}`);
    if (ratio > mejorPuntaje) {
      mejorPuntaje = ratio;
      mejor = m;
    }
  }

  // Con menos de la mitad de las señales no se afirma nada: es preferible "no sé
  // qué modelo es" a plantar una plantilla equivocada y tapar el lugar de al lado.
  if (mejorPuntaje < 0.5) {
    detalle.push("ningún modelo llega al mínimo: se lee sin plantilla");
    return { modelo: null, confianza: mejorPuntaje, detalle };
  }
  return { modelo: mejor, confianza: mejorPuntaje, detalle };
}
